import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 as TrashIcon } from 'lucide-react';
import {
  Gamepad2,
  Plus,
  ScrollText,
  Send,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { RolePicker } from '@/components/RolePicker';
import { CustomEmojiPicker } from '@/components/CustomEmojiPicker';
import { EmojiDisplay } from '@/components/EmojiDisplay';
import { DiscordMessagePreview } from '@/components/DiscordMessagePreview';
import { MediaPicker } from '@/components/MediaPicker';
import { useGuildRights } from '@/lib/access';
import { refreshPostedSoon } from '@/lib/posted';
import { PostedList } from '@/components/PostedList';
import { BackLink } from './_shared';

/**
 * Panels + Games-Katalog. Jede Export-Page ist eine eigene Route in der
 * „Panels"-Sidebar-Gruppe:
 *   - RulesPanelConfig / SupportRulesPanelConfig: PanelEditor (Embed-Felder
 *     + Save in DB + Post der aktuellen Def)
 *   - GamesCatalogConfig: Games-Katalog + Games-Setup-Button
 */

type PanelType = 'rules' | 'support-rules';

interface EmbedDef {
  title?: string;
  description?: string;
  color?: number;
  imageUrl?: string;
  thumbnailUrl?: string;
  footer?: { text?: string; iconUrl?: string };
}
interface ButtonDef {
  label: string;
  emoji?: string | null;
  customId: string;
  style: 'primary' | 'secondary' | 'success' | 'danger';
}
interface PanelDef {
  content?: string | null;
  embed?: EmbedDef | null;
  buttons?: ButtonDef[] | null;
  mode?: 'buttons' | 'select' | null;
  /** Rules only: single accept-button that hands out a role on click. */
  acceptButton?: AcceptButtonDef | null;
}
interface AcceptButtonDef {
  label: string;
  emoji: string | null;
  roleId: string | null;
  /** Given on join, removed on accept (bot: StartRole + AcceptRules). */
  startRoleId?: string | null;
}

interface RankOrMode {
  label: string;
  emoji?: string | null;
  /** Voice-channel user cap. 0 = unlimited, null/undefined = fall back to heuristic. */
  userLimit?: number | null;
}
interface GameDef {
  name: string;
  /** Optional custom Discord category + inner channel names. */
  categoryName?: string | null;
  panelChannelName?: string | null;
  searchChannelName?: string | null;
  ranks: Array<string | RankOrMode>;
  modes: Array<string | RankOrMode>;
  embed?: {
    title?: string | null;
    description?: string | null;
    color?: number | null;
    placeholder?: string | null;
    imageUrl?: string | null;
  } | null;
}

/** Same rule as the bot's getUserLimitForMode (gameCategories.js): explicit cap, else label heuristic. */
function previewUserLimit(e: RankOrMode): number {
  if (typeof e.userLimit === 'number') return Math.max(0, Math.min(99, e.userLimit));
  const m = e.label.toUpperCase();
  if (m.includes('DUO')) return 2;
  if (m.includes('TRIO')) return 3;
  if (m.includes('QUAD') || m.includes('SQUAD')) return 4;
  if (m.includes('SOLO')) return 1;
  return 5;
}

/** Legacy strings and new objects both allowed — normalise to objects for the editor. */
function toEntry(raw: string | RankOrMode): RankOrMode {
  if (typeof raw === 'string') return { label: raw, emoji: null, userLimit: null };
  return {
    label: raw.label,
    emoji: raw.emoji ?? null,
    userLimit: typeof raw.userLimit === 'number' ? raw.userLimit : null,
  };
}

export function RulesPanelConfig() {
  const { guildId } = useParams<{ guildId: string }>();
  return (
    <div className="mx-auto max-w-3xl">
      <BackLink guildId={guildId!} />
      <h1 className="mb-6 text-2xl font-semibold">Regeln-Panel</h1>
      <PanelEditor
        guildId={guildId!}
        type="rules"
        title="Server-Regeln"
        icon={ShieldCheck}
        description="Rules-Embed mit einem Accept-Button. User klickt → bekommt die gewählte Rolle. Regel-Text bearbeitest du oben im Embed."
        supportsAccept
      />
    </div>
  );
}

export function SupportRulesPanelConfig() {
  const { guildId } = useParams<{ guildId: string }>();
  return (
    <div className="mx-auto max-w-3xl">
      <BackLink guildId={guildId!} />
      <h1 className="mb-6 text-2xl font-semibold">Support-Regeln-Panel</h1>
      <PanelEditor
        guildId={guildId!}
        type="support-rules"
        title="Support-Regeln"
        icon={ScrollText}
        description="SupportRules-Embed ohne Buttons. Nur Text-Panel."
      />
    </div>
  );
}

export function GamesCatalogConfig() {
  const { guildId } = useParams<{ guildId: string }>();
  const canEdit = useGuildRights(guildId).can('games', 'edit');

  return (
    <div className="mx-auto max-w-3xl">
      <BackLink guildId={guildId!} />
      <h1 className="mb-6 text-2xl font-semibold">Games-Katalog</h1>

      <GamesCatalogCard guildId={guildId!} canEdit={canEdit} />

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Gamepad2 className="h-5 w-5 text-primary" />
            Games-Setup
          </CardTitle>
          <CardDescription>
            Erstellt eine Kategorie mit „channel-eröffnen" + „spieler-suche"-Channel
            und postet das Rank-Select-Menü. User wählt Rank → danach die Gruppengröße
            (Buttons aus den Modes) → Bot legt einen Voice-Channel mit passendem Limit an.
            Schon gepostete Panels behalten den alten Ablauf, bis du Setup neu ausführst.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <GamesSetupForm guildId={guildId!} disabled={!canEdit} />
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Gepostete Games-Panels</CardTitle>
          <CardDescription>
            „Aktualisieren“ baut das Panel aus dem aktuellen Katalog neu — z. B. nach neuen Ranks oder
            einem geänderten Bild. Channels bleiben, wie sie sind.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <PostedList guildId={guildId!} type="games" />
        </CardContent>
      </Card>
    </div>
  );
}

// --------------------------- Games Catalog ---------------------------

function GamesCatalogCard({ guildId, canEdit }: { guildId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const catalogQuery = useQuery({
    queryKey: ['games-catalog', guildId],
    queryFn: () =>
      api<{ games: GameDef[]; isDefault: boolean }>(`/api/guilds/${guildId}/games`),
  });

  const [games, setGames] = useState<GameDef[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (catalogQuery.data) {
      setGames(catalogQuery.data.games);
      setDirty(false);
    }
  }, [catalogQuery.data]);

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/api/guilds/${guildId}/games`, {
        method: 'PUT',
        body: JSON.stringify({ games: cleanGames(games) }),
      }),
    onSuccess: () => {
      setDirty(false);
      setError(null);
      qc.invalidateQueries({ queryKey: ['games-catalog', guildId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const patch = (i: number, up: Partial<GameDef>) => {
    setGames((prev) => prev.map((g, j) => (j === i ? { ...g, ...up } : g)));
    setDirty(true);
  };
  const remove = (i: number) => {
    setGames((prev) => prev.filter((_, j) => j !== i));
    setDirty(true);
  };
  const add = () => {
    setGames((prev) => [
      ...prev,
      { name: 'Neues Game', ranks: [], modes: [] },
    ]);
    setDirty(true);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Gamepad2 className="h-5 w-5 text-primary" />
          Games-Katalog
        </CardTitle>
        <CardDescription>
          Definiere welche Games du für Games-Setup nutzen kannst. Ranks stehen im
          Select-Menü, Modes werden danach als Gruppengrößen-Buttons angeboten (User-Limit
          pro Mode). Discord-Limit: je 25 Ranks und Modes pro Game.
          {catalogQuery.data?.isDefault && (
            <> Aktuell noch die Standard-Vorlagen — Speichern legt deine Version an.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {catalogQuery.isLoading && <p className="text-muted-foreground">Lade…</p>}
        {games.map((g, i) => (
          <GameEditor
            key={i}
            guildId={guildId}
            game={g}
            onChange={(u) => patch(i, u)}
            onRemove={() => remove(i)}
            disabled={!canEdit}
          />
        ))}
        {games.length === 0 && !catalogQuery.isLoading && (
          <p className="text-sm text-muted-foreground">Noch keine Games.</p>
        )}
        {canEdit && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button variant="outline" size="sm" onClick={add}>
              <Plus className="mr-1 h-3 w-3" /> Game hinzufügen
            </Button>
            <div className="flex items-center gap-2">
              {error && <span className="text-sm text-destructive">{error}</span>}
              {saveMut.isSuccess && !dirty && (
                <span className="text-sm text-success">Gespeichert ✓</span>
              )}
              <Button
                size="sm"
                disabled={!dirty || saveMut.isPending}
                onClick={() => saveMut.mutate()}
              >
                {saveMut.isPending ? 'Speichere…' : 'Katalog speichern'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GameEditor({
  guildId,
  game,
  onChange,
  onRemove,
  disabled,
}: {
  guildId: string;
  game: GameDef;
  onChange: (u: Partial<GameDef>) => void;
  onRemove: () => void;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const totalOptions = game.ranks.length + game.modes.length;
  return (
    <div className="rounded-md border p-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-sm font-medium hover:text-primary"
        >
          {open ? '▾' : '▸'} {game.name || '(unbenannt)'}
        </button>
        <span className="text-xs text-muted-foreground">
          {game.ranks.length} Ranks · {game.modes.length} Modes ·{' '}
          <span className={totalOptions > 25 ? 'text-destructive' : ''}>
            {totalOptions}/25
          </span>
        </span>
        <div className="ml-auto">
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            disabled={disabled}
            title="Game löschen"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={game.name}
              onChange={(e) => onChange({ name: e.target.value })}
              maxLength={80}
              disabled={disabled}
            />
          </div>
          <div>
            <Label>Kategorie-Name in Discord (optional)</Label>
            <Input
              value={game.categoryName ?? ''}
              onChange={(e) => onChange({ categoryName: e.target.value || null })}
              maxLength={100}
              disabled={disabled}
              placeholder={`✦▬▌${game.name || 'GAME'}▐▬✦➤`}
            />
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div>
              <Label>Panel-Channel-Name (optional)</Label>
              <Input
                value={game.panelChannelName ?? ''}
                onChange={(e) =>
                  onChange({ panelChannelName: e.target.value || null })
                }
                maxLength={100}
                disabled={disabled}
                placeholder="【🌐】channel-eröffnen"
              />
            </div>
            <div>
              <Label>Search-Channel-Name (optional)</Label>
              <Input
                value={game.searchChannelName ?? ''}
                onChange={(e) =>
                  onChange({ searchChannelName: e.target.value || null })
                }
                maxLength={100}
                disabled={disabled}
                placeholder="【🕵】spieler-suche"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Namen werden nur beim ersten Setup verwendet. Kanäle danach in Discord
            beliebig umbenennen — die Zuordnung merkt sich der Bot per ID.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <EntryListEditor
              label="Ranks"
              entries={game.ranks.map(toEntry)}
              onChange={(entries) => onChange({ ranks: entries })}
              disabled={disabled}
            />
            <EntryListEditor
              label="Modes (Gruppengrößen)"
              entries={game.modes.map(toEntry)}
              onChange={(entries) => onChange({ modes: entries })}
              disabled={disabled}
            />
          </div>

          <div className="rounded-md border bg-muted/30 p-3">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Panel-Anpassungen (optional)
            </div>
            <div className="space-y-2">
              <div>
                <Label>Embed-Titel</Label>
                <Input
                  value={game.embed?.title ?? ''}
                  onChange={(e) =>
                    onChange({
                      embed: { ...(game.embed ?? {}), title: e.target.value || null },
                    })
                  }
                  maxLength={256}
                  placeholder={`🎮 ${game.name} — Voice-Channel erstellen`}
                  disabled={disabled}
                />
              </div>
              <div>
                <Label>Embed-Beschreibung</Label>
                <Textarea emoji
                  value={game.embed?.description ?? ''}
                  onChange={(e) =>
                    onChange({
                      embed: { ...(game.embed ?? {}), description: e.target.value || null },
                    })
                  }
                  maxLength={4096}
                  placeholder="Wähle unten deinen Rank oder Modus…"
                  className="min-h-[60px]"
                  disabled={disabled}
                />
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div>
                  <Label>Embed-Farbe</Label>
                  <input
                    type="color"
                    value={intToHex(game.embed?.color ?? 0x5865f2)}
                    onChange={(e) =>
                      onChange({
                        embed: {
                          ...(game.embed ?? {}),
                          color: hexToInt(e.target.value),
                        },
                      })
                    }
                    disabled={disabled}
                    className="h-10 w-full rounded-md border border-input bg-background"
                  />
                </div>
                <div>
                  <Label>Select-Placeholder</Label>
                  <Input
                    value={game.embed?.placeholder ?? ''}
                    onChange={(e) =>
                      onChange({
                        embed: {
                          ...(game.embed ?? {}),
                          placeholder: e.target.value || null,
                        },
                      })
                    }
                    maxLength={150}
                    placeholder="Rank oder Modus wählen"
                    disabled={disabled}
                  />
                </div>
              </div>
              <div>
                <Label>Großes Bild URL (optional)</Label>
                <div className="flex gap-2">
                  <Input
                    value={game.embed?.imageUrl ?? ''}
                    onChange={(e) =>
                      onChange({
                        embed: {
                          ...(game.embed ?? {}),
                          imageUrl: e.target.value || null,
                        },
                      })
                    }
                    maxLength={2000}
                    placeholder="https://…"
                    disabled={disabled}
                  />
                  <MediaPicker
                    guildId={guildId}
                    currentUrl={game.embed?.imageUrl ?? undefined}
                    onSelect={(url) =>
                      onChange({
                        embed: {
                          ...(game.embed ?? {}),
                          imageUrl: window.location.origin + url,
                        },
                      })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <DiscordMessagePreview
              hint="So sieht das Panel im Voice-Setup-Channel aus (das Select-Menü kommt darunter)."
              embed={{
                title:
                  game.embed?.title ||
                  `🎮 ${game.name || 'Neues Game'} — Voice-Channel erstellen`,
                description:
                  game.embed?.description ||
                  (game.ranks.length > 0 && game.modes.length > 0
                    ? 'Wähle unten deinen Rank, danach die Gruppengröße.\nDer Bot legt einen passenden Voice-Channel für dich an.'
                    : 'Wähle unten deinen Rank oder Modus.\nDer Bot legt einen passenden Voice-Channel für dich an.'),
                color: game.embed?.color ?? 0x5865f2,
                imageUrl: game.embed?.imageUrl || undefined,
              }}
            />
            {/* Mirrors runGamesSetup: select = ranks (or modes if no ranks). */}
            <div className="mt-2 flex flex-wrap gap-1">
              {(game.ranks.length > 0 ? game.ranks : game.modes)
                .map(toEntry)
                .filter((e) => e.label.trim())
                .slice(0, 25)
                .map((e, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 rounded-md border border-white/20 bg-[#2b2d31] px-2 py-1 text-xs text-white/80"
                  >
                    {e.emoji && <EmojiDisplay value={e.emoji} size={12} />}
                    {e.label}
                  </span>
                ))}
              {game.ranks.length + game.modes.length === 0 && (
                <span className="text-xs text-muted-foreground">
                  (Keine Ranks oder Modes definiert)
                </span>
              )}
            </div>
            {game.ranks.length > 0 && game.modes.length > 0 && (
              <div className="mt-2">
                <div className="mb-1 text-[11px] text-muted-foreground">
                  Nach der Rank-Wahl (nur für den User sichtbar):
                </div>
                <div className="flex flex-wrap gap-2">
                  {game.modes
                    .map(toEntry)
                    .filter((e) => e.label.trim())
                    .slice(0, 25)
                    .map((e, i) => {
                      const limit = previewUserLimit(e);
                      return (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1.5 rounded-md bg-[#5865f2] px-3 py-1.5 text-xs text-white"
                        >
                          {e.emoji && <EmojiDisplay value={e.emoji} size={12} />}
                          {e.label} 👥 {limit === 0 ? '∞' : limit}
                        </span>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Row editor for ranks/modes — one line per entry with label input + emoji
 * picker. Emits objects; the backend accepts either objects or plain strings
 * (legacy). Empty labels are dropped on save.
 */
function EntryListEditor({
  label,
  entries,
  onChange,
  disabled,
}: {
  label: string;
  entries: RankOrMode[];
  onChange: (entries: RankOrMode[]) => void;
  disabled: boolean;
}) {
  const update = (i: number, patch: Partial<RankOrMode>) =>
    onChange(entries.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  return (
    <div>
      <Label>
        {label} ({entries.length}/25)
      </Label>
      <div className="mt-1 space-y-1">
        {entries.map((e, i) => (
          <div key={i} className="flex items-center gap-1">
            <CustomEmojiPicker
              value={e.emoji ?? null}
              onChange={(v) => update(i, { emoji: v })}
              buttonLabel={e.emoji ? undefined : ''}
            />
            <Input
              value={e.label}
              onChange={(ev) => update(i, { label: ev.target.value })}
              maxLength={100}
              disabled={disabled}
              className="h-9 flex-1 text-sm"
              placeholder="Name"
            />
            <Input
              type="number"
              min={0}
              max={99}
              value={e.userLimit ?? ''}
              onChange={(ev) => {
                const v = ev.target.value;
                update(i, { userLimit: v === '' ? null : Math.max(0, Math.min(99, Number(v))) });
              }}
              disabled={disabled}
              className="h-9 w-16 text-sm"
              placeholder="Limit"
              title="Voice-Channel-User-Limit (0 = unbegrenzt, leer = automatisch)"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onChange(entries.filter((_, j) => j !== i))}
              disabled={disabled}
              className="h-9 w-9 shrink-0"
              title="Zeile entfernen"
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {entries.length < 25 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onChange([...entries, { label: '', emoji: null }])}
            disabled={disabled}
            className="h-8"
          >
            <Plus className="mr-1 h-3 w-3" /> {label.slice(0, -1)} hinzufügen
          </Button>
        )}
        <p className="text-[10px] text-muted-foreground">
          User-Limit: leer = automatisch (DUO=2, TRIO=3, …); 0 = unbegrenzt.
        </p>
      </div>
    </div>
  );
}

function cleanGames(games: GameDef[]): GameDef[] {
  const cleanEntries = (arr: Array<string | RankOrMode>): RankOrMode[] =>
    arr
      .map(toEntry)
      .filter((e) => e.label.trim().length > 0)
      .map((e) => ({
        label: e.label.trim(),
        emoji: e.emoji || null,
        userLimit:
          typeof e.userLimit === 'number' && Number.isFinite(e.userLimit)
            ? Math.max(0, Math.min(99, e.userLimit))
            : null,
      }))
      .slice(0, 25);
  return games
    .filter((g) => g.name.trim().length > 0)
    .map((g) => ({
      name: g.name.trim(),
      categoryName: g.categoryName?.trim() || null,
      panelChannelName: g.panelChannelName?.trim() || null,
      searchChannelName: g.searchChannelName?.trim() || null,
      ranks: cleanEntries(g.ranks),
      modes: cleanEntries(g.modes),
      embed:
        g.embed &&
        (g.embed.title ||
          g.embed.description ||
          g.embed.color != null ||
          g.embed.placeholder ||
          g.embed.imageUrl)
          ? {
              title: g.embed.title?.trim() || null,
              description: g.embed.description?.trim() || null,
              color: g.embed.color ?? null,
              placeholder: g.embed.placeholder?.trim() || null,
              imageUrl: g.embed.imageUrl?.trim() || null,
            }
          : null,
    }));
}

// --------------------------- Panel Editor ---------------------------

function PanelEditor({
  guildId,
  type,
  title,
  icon: Icon,
  description,
  supportsButtons,
  supportsAccept,
}: {
  guildId: string;
  type: PanelType;
  title: string;
  icon: typeof Gamepad2;
  description: string;
  supportsButtons?: boolean;
  /** Rules-Panel single-Accept-Button (with role) mode. */
  supportsAccept?: boolean;
}) {
  const qc = useQueryClient();
  const panelQuery = useQuery({
    queryKey: ['panel', guildId, type],
    queryFn: () => api<{ type: string; panel: PanelDef | null }>(`/api/guilds/${guildId}/panels/${type}`),
  });

  const [content, setContent] = useState('');
  const [embed, setEmbed] = useState<EmbedDef>({ color: 0x5865f2 });
  const [buttons, setButtons] = useState<ButtonDef[]>([]);
  const [mode, setMode] = useState<'buttons' | 'select'>('buttons');
  const [acceptButton, setAcceptButton] = useState<AcceptButtonDef>({
    label: 'Regeln akzeptieren',
    emoji: '✅',
    roleId: null,
    startRoleId: null,
  });
  const [dirty, setDirty] = useState(false);
  const [channelId, setChannelId] = useState<string | null>(null);

  // Populate from server state on load.
  useEffect(() => {
    const p = panelQuery.data?.panel;
    if (p) {
      setContent(p.content ?? '');
      setEmbed(p.embed ?? { color: 0x5865f2 });
      setButtons(p.buttons ?? []);
      if (p.mode === 'buttons' || p.mode === 'select') setMode(p.mode);
      if (p.acceptButton) {
        setAcceptButton({
          label: p.acceptButton.label || 'Regeln akzeptieren',
          emoji: p.acceptButton.emoji ?? null,
          roleId: p.acceptButton.roleId ?? null,
          startRoleId: p.acceptButton.startRoleId ?? null,
        });
      }
    }
    setDirty(false);
  }, [panelQuery.data]);

  const saveMut = useMutation({
    mutationFn: () => {
      const body: PanelDef = {
        content: content.trim() || null,
        embed: hasEmbedContent(embed) ? cleanEmbed(embed) : null,
        buttons: supportsButtons ? buttons : null,
        mode: supportsButtons ? mode : null,
        acceptButton: supportsAccept
          ? {
              label: acceptButton.label.trim() || 'Regeln akzeptieren',
              emoji: acceptButton.emoji ?? null,
              roleId: acceptButton.roleId ?? null,
              startRoleId: acceptButton.startRoleId ?? null,
            }
          : null,
      };
      return api(`/api/guilds/${guildId}/panels/${type}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['panel', guildId, type] });
    },
  });

  const postMut = useMutation({
    mutationFn: () =>
      api(`/api/actions/${type}-post`, {
        method: 'POST',
        body: JSON.stringify({ guildId, channelId }),
      }),
    onSuccess: () => refreshPostedSoon(qc, guildId),
  });

  // Same role twice would hand it out on join and take it away on accept.
  const roleConflict =
    !!supportsAccept && !!acceptButton.startRoleId && acceptButton.startRoleId === acceptButton.roleId;

  const patchEmbed = (patch: Partial<EmbedDef>) => {
    setEmbed((e) => ({ ...e, ...patch }));
    setDirty(true);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Icon className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Content über dem Embed */}
        <div>
          <Label>Text über dem Embed (optional)</Label>
          <Textarea emoji
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              setDirty(true);
            }}
            className="min-h-[60px]"
            maxLength={2000}
          />
        </div>

        {/* Embed-Felder */}
        <div className="rounded-md border p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Embed
          </div>
          <div className="space-y-2">
            <div>
              <Label>Titel</Label>
              <Input
                value={embed.title ?? ''}
                onChange={(e) => patchEmbed({ title: e.target.value })}
                maxLength={256}
              />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea emoji
                value={embed.description ?? ''}
                onChange={(e) => patchEmbed({ description: e.target.value })}
                className="min-h-[100px]"
                maxLength={4096}
              />
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div>
                <Label>Farbe</Label>
                <input
                  type="color"
                  value={intToHex(embed.color ?? 0x5865f2)}
                  onChange={(e) => patchEmbed({ color: hexToInt(e.target.value) })}
                  className="h-10 w-full rounded-md border border-input bg-background"
                />
              </div>
              <div>
                <Label>Thumbnail URL</Label>
                <div className="flex gap-2">
                  <Input
                    value={embed.thumbnailUrl ?? ''}
                    onChange={(e) => patchEmbed({ thumbnailUrl: e.target.value })}
                    placeholder="https://…"
                  />
                  <MediaPicker
                    guildId={guildId}
                    currentUrl={embed.thumbnailUrl}
                    onSelect={(url) => patchEmbed({ thumbnailUrl: window.location.origin + url })}
                  />
                </div>
              </div>
            </div>
            <div>
              <Label>Großes Bild URL</Label>
              <div className="flex gap-2">
                <Input
                  value={embed.imageUrl ?? ''}
                  onChange={(e) => patchEmbed({ imageUrl: e.target.value })}
                  placeholder="https://…"
                />
                <MediaPicker
                  guildId={guildId}
                  currentUrl={embed.imageUrl}
                  onSelect={(url) => patchEmbed({ imageUrl: window.location.origin + url })}
                />
              </div>
            </div>
            <div>
              <Label>Footer-Text</Label>
              <Input
                value={embed.footer?.text ?? ''}
                onChange={(e) =>
                  patchEmbed({
                    footer: { ...(embed.footer ?? {}), text: e.target.value },
                  })
                }
                maxLength={2048}
              />
            </div>
          </div>
        </div>

        {/* Mode toggle (only when panel has button-vs-select choice). */}
        {supportsButtons && (
          <div>
            <Label>Darstellung</Label>
            <div className="mt-1 flex gap-1">
              {(['buttons', 'select'] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => {
                    setMode(m);
                    setDirty(true);
                  }}
                  className={
                    'rounded-md border px-3 py-1.5 text-xs ' +
                    (mode === m
                      ? 'border-primary bg-primary/15 text-primary'
                      : 'bg-background hover:bg-accent')
                  }
                >
                  {m === 'buttons' ? 'Buttons' : 'Select-Menü'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Buttons */}
        {supportsButtons && (
          <ButtonsEditor
            buttons={buttons}
            onChange={(bs) => {
              setButtons(bs);
              setDirty(true);
            }}
          />
        )}

        {/* Accept-Button (Rules): einzelner Button + wählbare Rolle */}
        {supportsAccept && (
          <AcceptButtonEditor
            guildId={guildId}
            value={acceptButton}
            onChange={(v) => {
              setAcceptButton(v);
              setDirty(true);
            }}
          />
        )}

        {/* Save + Post */}
        <div className="mt-4 flex flex-wrap items-end gap-3 rounded-md border bg-muted/40 p-3">
          <div className="flex flex-1 items-end gap-2">
            <div className="min-w-[220px] flex-1">
              <Label>Ziel-Channel</Label>
              <ChannelPicker
                guildId={guildId}
                value={channelId}
                onChange={setChannelId}
                types={[CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT]}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={!dirty || roleConflict || saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              {saveMut.isPending ? 'Speichere…' : dirty ? 'Speichern' : 'Gespeichert'}
            </Button>
            <Button
              disabled={!channelId || postMut.isPending}
              onClick={() => postMut.mutate()}
              className="gap-1"
            >
              <Send className="h-4 w-4" />
              {postMut.isPending ? 'Poste…' : 'Posten'}
            </Button>
          </div>
        </div>
        {(saveMut.error || postMut.error) && (
          <p className="text-sm text-destructive">
            {(saveMut.error || postMut.error) instanceof Error
              ? ((saveMut.error || postMut.error) as Error).message
              : 'Fehler'}
          </p>
        )}
        {postMut.isSuccess && (
          <p className="text-sm text-success">Panel gepostet ✓</p>
        )}

        <div className="border-t pt-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Bereits gepostet
          </div>
          <PostedList
            guildId={guildId}
            type={type}
            updateBlocked={
              dirty
                ? 'Ungespeicherte Änderungen — erst speichern, „Aktualisieren“ nimmt die gespeicherte Version.'
                : null
            }
          />
        </div>

        <div className="border-t pt-4">
          <DiscordMessagePreview
            hint="So sieht das Panel im Ziel-Channel aus. Buttons/Optionen werden als Discord-Komponenten unter dem Embed angezeigt."
            content={content}
            embed={
              hasEmbedContent(embed)
                ? {
                    title: embed.title || undefined,
                    description: embed.description || undefined,
                    color: embed.color ?? 0x5865f2,
                    imageUrl: embed.imageUrl || undefined,
                    thumbnailUrl: embed.thumbnailUrl || undefined,
                    footer: embed.footer?.text ? { text: embed.footer.text } : undefined,
                  }
                : undefined
            }
          />
          {supportsAccept && (
            <div className="mt-2 flex flex-wrap gap-2">
              <span
                className={
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-white ' +
                  (acceptButton.roleId ? 'bg-[#248046]' : 'bg-[#4e5058]')
                }
              >
                {acceptButton.emoji && <EmojiDisplay value={acceptButton.emoji} size={14} />}
                {acceptButton.label || 'Regeln akzeptieren'}
              </span>
            </div>
          )}
          {supportsButtons && buttons.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {buttons
                .filter((b) => b.label.trim())
                .map((b, i) => (
                  <span
                    key={i}
                    className={
                      'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-white ' +
                      buttonStyleClass(b.style)
                    }
                  >
                    {b.emoji && <EmojiDisplay value={b.emoji} size={14} />}
                    {b.label}
                  </span>
                ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function buttonStyleClass(style: ButtonDef['style']): string {
  switch (style) {
    case 'primary':
      return 'bg-[#5865f2]';
    case 'success':
      return 'bg-[#248046]';
    case 'danger':
      return 'bg-[#da373c]';
    case 'secondary':
    default:
      return 'bg-[#4e5058]';
  }
}

// --------------------------- Buttons Editor ---------------------------

function ButtonsEditor({
  buttons,
  onChange,
}: {
  buttons: ButtonDef[];
  onChange: (bs: ButtonDef[]) => void;
}) {
  const update = (i: number, patch: Partial<ButtonDef>) =>
    onChange(buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Buttons ({buttons.length} / 25)
      </div>
      <div className="space-y-2">
        {buttons.map((b, i) => (
          <div key={i} className="flex flex-wrap items-end gap-2 rounded-md border bg-muted/30 p-2">
            <div className="min-w-[120px] flex-1">
              <Label>Label</Label>
              <Input
                value={b.label}
                onChange={(e) => update(i, { label: e.target.value })}
                maxLength={80}
              />
            </div>
            <div>
              <Label>Emoji</Label>
              <CustomEmojiPicker
                value={b.emoji ?? null}
                onChange={(v) => update(i, { emoji: v })}
                buttonLabel={b.emoji ? undefined : 'Emoji'}
              />
            </div>
            <div className="min-w-[110px]">
              <Label>Style</Label>
              <select
                value={b.style}
                onChange={(e) => update(i, { style: e.target.value as ButtonDef['style'] })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="primary">Primary (blau)</option>
                <option value="secondary">Secondary (grau)</option>
                <option value="success">Success (grün)</option>
                <option value="danger">Danger (rot)</option>
              </select>
            </div>
            <div className="min-w-[140px]">
              <Label>Custom-ID</Label>
              <Input
                value={b.customId}
                onChange={(e) => update(i, { customId: e.target.value })}
                maxLength={100}
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onChange(buttons.filter((_, j) => j !== i))}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {buttons.length < 25 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              onChange([
                ...buttons,
                { label: 'Button', emoji: null, customId: 'panel-btn', style: 'primary' },
              ])
            }
          >
            <Plus className="mr-1 h-3 w-3" /> Button hinzufügen
          </Button>
        )}
      </div>
    </div>
  );
}

// --------------------------- Accept-Button Editor ---------------------------

function AcceptButtonEditor({
  guildId,
  value,
  onChange,
}: {
  guildId: string;
  value: AcceptButtonDef;
  onChange: (v: AcceptButtonDef) => void;
}) {
  const conflict = !!value.startRoleId && value.startRoleId === value.roleId;
  return (
    <div className="rounded-md border p-3">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Accept-Button
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <Label>Button-Text</Label>
          <Input
            value={value.label}
            onChange={(e) => onChange({ ...value, label: e.target.value })}
            maxLength={80}
            placeholder="Regeln akzeptieren"
          />
        </div>
        <div>
          <Label>Emoji</Label>
          <CustomEmojiPicker
            value={value.emoji}
            onChange={(v) => onChange({ ...value, emoji: v })}
            buttonLabel={value.emoji ? undefined : 'Emoji'}
          />
        </div>
        <div>
          <Label>Rolle bei Klick</Label>
          <RolePicker
            guildId={guildId}
            value={value.roleId}
            onChange={(v) => onChange({ ...value, roleId: v })}
          />
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        {value.roleId
          ? 'User klickt Button → bekommt die gewählte Rolle.'
          : 'Ohne Rolle: Button funktioniert nur als Bestätigung ohne Rollen-Vergabe.'}
      </p>
      <div className="mt-3 border-t pt-3">
        <Label>Startrolle (optional)</Label>
        <div className="mt-1 sm:max-w-xs">
          <RolePicker
            guildId={guildId}
            value={value.startRoleId ?? null}
            onChange={(v) => onChange({ ...value, startRoleId: v })}
            placeholder="— keine Startrolle —"
          />
        </div>
        {conflict ? (
          <p className="mt-2 text-xs text-destructive">
            Startrolle und Rolle bei Klick müssen verschieden sein.
          </p>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            {value.startRoleId
              ? 'Neue Mitglieder bekommen diese Rolle beim Beitreten. Sobald sie die Regeln akzeptieren, wird sie wieder entfernt. Gilt sofort, ohne das Panel neu zu posten.'
              : 'Wird beim Beitreten vergeben und beim Akzeptieren wieder entfernt — z. B. um bis dahin nur den Regel-Channel sichtbar zu machen.'}
          </p>
        )}
      </div>
    </div>
  );
}

// --------------------------- Games Setup Form ---------------------------

function GamesSetupForm({
  guildId,
  disabled,
}: {
  guildId: string;
  disabled: boolean;
}) {
  // Load games from the same catalog endpoint so the dropdown reflects
  // whatever the admin has customised.
  const catalogQuery = useQuery({
    queryKey: ['games-catalog', guildId],
    queryFn: () =>
      api<{ games: GameDef[]; isDefault: boolean }>(`/api/guilds/${guildId}/games`),
  });
  const games = catalogQuery.data?.games ?? [];
  const [gameName, setGameName] = useState<string>('');

  useEffect(() => {
    if (!gameName && games.length > 0) setGameName(games[0].name);
  }, [games, gameName]);

  const qc = useQueryClient();
  const setupMut = useMutation({
    mutationFn: () =>
      api('/api/actions/games-setup', {
        method: 'POST',
        body: JSON.stringify({ guildId, gameName }),
      }),
    onSuccess: () => refreshPostedSoon(qc, guildId),
  });

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[200px] flex-1">
        <Label>Spiel</Label>
        <select
          value={gameName}
          onChange={(e) => setGameName(e.target.value)}
          disabled={disabled || games.length === 0}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm disabled:opacity-60"
        >
          {games.map((g) => (
            <option key={g.name} value={g.name}>
              {g.name}
            </option>
          ))}
        </select>
      </div>
      <Button
        disabled={disabled || setupMut.isPending || !gameName}
        onClick={() => {
          if (confirm(`Kategorie + Channels für ${gameName} anlegen?`)) {
            setupMut.mutate();
          }
        }}
      >
        {setupMut.isPending ? 'Setze auf…' : 'Setup starten'}
      </Button>
      {disabled && (
        <span className="text-xs text-muted-foreground">
          Nur Admins können Games-Setup ausführen.
        </span>
      )}
      {setupMut.isSuccess && (
        <span className="text-sm text-success">Setup fertig ✓</span>
      )}
      {setupMut.error && (
        <span className="text-sm text-destructive">
          {(setupMut.error as Error).message}
        </span>
      )}
    </div>
  );
}

// --------------------------- Helpers ---------------------------

function intToHex(n: number): string {
  return '#' + (n & 0xffffff).toString(16).padStart(6, '0');
}
function hexToInt(hex: string): number {
  return parseInt(hex.replace(/^#/, ''), 16) & 0xffffff;
}
function hasEmbedContent(e: EmbedDef): boolean {
  return !!(e.title || e.description || e.imageUrl || e.thumbnailUrl || e.footer?.text);
}
function cleanEmbed(e: EmbedDef): EmbedDef {
  const strip = (v: string | undefined) =>
    v && v.trim().length > 0 ? v.trim() : undefined;
  const footer =
    e.footer?.text || e.footer?.iconUrl
      ? {
          text: strip(e.footer?.text),
          iconUrl: strip(e.footer?.iconUrl),
        }
      : undefined;
  return {
    title: strip(e.title),
    description: strip(e.description),
    color: e.color,
    imageUrl: strip(e.imageUrl),
    thumbnailUrl: strip(e.thumbnailUrl),
    footer,
  };
}
